import React, {
  createContext,
  useState,
  useContext,
  useEffect,
  useCallback,
} from 'react';
import api, { setOnTokenExpired } from '../services/api';

const AuthContext = createContext(null);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within AuthProvider');
  }
  return context;
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [token, setToken] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [showSessionExpiredModal, setShowSessionExpiredModal] = useState(false);

  // Verificar si el token está expirado
  const isTokenExpired = useCallback((tokenString) => {
    if (!tokenString) return true;
    try {
      const payload = JSON.parse(atob(tokenString.split('.')[1]));
      const exp = payload.exp * 1000;
      return Date.now() >= exp;
    } catch {
      return true;
    }
  }, []);

  // Cargar token almacenado
  useEffect(() => {
    const storedToken = localStorage.getItem('access_token');
    const storedUser = localStorage.getItem('user');

    if (storedToken && storedUser && !isTokenExpired(storedToken)) {
      setToken(storedToken);
      setUser(JSON.parse(storedUser));
      api.defaults.headers.common['Authorization'] = `Bearer ${storedToken}`;
    } else if (storedToken && isTokenExpired(storedToken)) {
      // Token expirado, limpiar
      localStorage.removeItem('access_token');
      localStorage.removeItem('user');
    }
    setLoading(false);
  }, [isTokenExpired]);

  // Configurar callback para token expirado
  useEffect(() => {
    setOnTokenExpired(() => handleTokenExpired);
    return () => setOnTokenExpired(null);
  }, []);

  // Verificar expiración periódicamente
  useEffect(() => {
    if (!token) return;

    const checkExpiration = () => {
      if (isTokenExpired(token)) {
        handleTokenExpired();
      }
    };

    // Verificar cada 30 segundos
    const intervalId = setInterval(checkExpiration, 30000);
    return () => clearInterval(intervalId);
  }, [token, isTokenExpired]);

  const handleTokenExpired = useCallback(() => {
    // Limpiar datos
    localStorage.removeItem('access_token');
    localStorage.removeItem('user');
    delete api.defaults.headers.common['Authorization'];

    setToken(null);
    setUser(null);
    setShowSessionExpiredModal(true);
  }, []);

  const closeSessionExpiredModal = useCallback(() => {
    setShowSessionExpiredModal(false);
  }, []);

  const login = async (username, password) => {
    setError(null);
    try {
      const response = await api.post('/administradores/login', {
        username,
        password,
      });

      const { access_token, administrador } = response.data;

      setToken(access_token);
      setUser(administrador);
      setShowSessionExpiredModal(false);

      localStorage.setItem('access_token', access_token);
      localStorage.setItem('user', JSON.stringify(administrador));

      api.defaults.headers.common['Authorization'] = `Bearer ${access_token}`;

      return { success: true };
    } catch (err) {
      const errorMessage =
        err.response?.data?.detail || 'Error al iniciar sesión';
      setError(errorMessage);
      return { success: false, error: errorMessage };
    }
  };

  const logout = useCallback(() => {
    setToken(null);
    setUser(null);
    setShowSessionExpiredModal(false);
    localStorage.removeItem('access_token');
    localStorage.removeItem('user');
    delete api.defaults.headers.common['Authorization'];
  }, []);

  const value = {
    user,
    token,
    loading,
    error,
    showSessionExpiredModal,
    login,
    logout,
    closeSessionExpiredModal,
    isAuthenticated: !!token,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};
